import React from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import Pagination from "shared/Pagination";
import RadioButton from "shared/@forms/RadioButton";
import {
  products as productsApi,
  categories as categoriesApi,
  normalizeTag,
  errorMessage,
} from "shared/api";
import FilterSidebar from "../../../components/FilterSidebar";
import ProductItemCard from "../ProductItemCard";
import {
  filterGroups,
  toListItem,
  dropRetiredStatuses,
  DEFAULT_FILTER_MODES,
} from "../../productListItem";
import ClearSearchButton from "shared/@forms/ClearSearchButton";
import styles from "./ProductCatalogBrowser.module.css";

const TOP_TAG_COUNT = 20;

/**
 * Shared product-catalog browser used by both the Store list page and the
 * Order Management → Create Order catalog. Owns search, filters (incl. AND/OR
 * match modes and clear controls), sort, pagination and the mock-backed fetch
 * so the two pages render identical catalogs. The only per-page differences are
 * supplied via props:
 *   - `renderItemActions(product)` → the right-hand controls on each row
 *     (Edit/Archive/Duplicate on the Store, quantity + Add To Cart on orders).
 *   - `onItemOpen(product)` → optional; makes the thumbnail and title a second
 *     route to the row's primary action, which matters once the action column
 *     has scrolled out of reach on a narrow list.
 *   - `toolbarActions` → extra buttons in the search row (Add New Product /
 *     View Cart).
 * Anything that sits *above* the catalog (e.g. the IBO header) is rendered by
 * the caller around this component.
 */
export function ProductCatalogBrowser({
  renderItemActions,
  onItemOpen,
  actionColWidth,
  toolbarActions,
  searchPlaceholder,
  initialFilters,
  initialFilterModes,
  refreshKey,
}) {
  // --- URL-sync: read initial state from query params --------------------------
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const readInt = (k, d) => {
    const v = urlParams.get(k);
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : d;
  };
  const readJson = (k, d) => {
    const v = urlParams.get(k);
    if (!v) return d;
    try {
      const parsed = JSON.parse(v);
      // A crafted `?f=null` / `?fm=null` parses successfully to `null`, which
      // would later blow up on `selectedFilters.tag` / `Object.keys(...)` and
      // white-screen the catalog. Only accept a real (non-array) object.
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : d;
    } catch {
      return d;
    }
  };

  const urlPage = readInt("p", 1);
  const urlPageSize = readInt("ps", 50);
  const urlSortRaw = urlParams.get("sort") || "created";
  const urlSortOrder = urlSortRaw.endsWith("_asc") ? "asc" : "desc";
  const urlSortField = urlSortRaw.startsWith("popularity")
    ? "popularity"
    : urlSortRaw.startsWith("updated")
    ? "updated"
    : urlSortRaw.startsWith("price")
    ? "price"
    : urlSortRaw.startsWith("product")
    ? "product"
    : "created";
  const urlPopWin = urlParams.get("pw") || "all";

  const [searchTerm, setSearchTerm] = React.useState(urlParams.get("q") || "");
  const [searchDraft, setSearchDraft] = React.useState(
    urlParams.get("q") || ""
  );
  const searchTimerRef = React.useRef(null);
  const [selectedFilters, setSelectedFilters] = React.useState(() =>
    dropRetiredStatuses(readJson("f", initialFilters), initialFilters)
  );
  const [filterModes, setFilterModes] = React.useState(
    readJson("fm", initialFilterModes)
  );
  const [sortField, setSortField] = React.useState(urlSortField);
  const [sortOrder, setSortOrder] = React.useState(urlSortOrder);
  const [popularityWindow, setPopularityWindow] = React.useState(urlPopWin);
  const [sortMenuOpen, setSortMenuOpen] = React.useState(false);
  const [tagOptions, setTagOptions] = React.useState([]);
  const [typeOptions, setTypeOptions] = React.useState([]);
  const [categoryOptions, setCategoryOptions] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [page, setPage] = React.useState(urlPage);
  const [total, setTotal] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(urlPageSize);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const reqIdRef = React.useRef(0);

  // Push state changes back to URL query params (debounced to avoid spam)
  const urlSyncTimer = React.useRef(null);
  React.useEffect(() => {
    if (urlSyncTimer.current) clearTimeout(urlSyncTimer.current);
    urlSyncTimer.current = setTimeout(() => {
      const params = new URLSearchParams();
      if (searchTerm) params.set("q", searchTerm);
      if (Object.keys(selectedFilters).length)
        params.set("f", JSON.stringify(selectedFilters));
      if (JSON.stringify(filterModes) !== JSON.stringify(DEFAULT_FILTER_MODES))
        params.set("fm", JSON.stringify(filterModes));
      if (page > 1) params.set("p", String(page));
      if (pageSize !== 50) params.set("ps", String(pageSize));
      const s =
        sortField === "popularity"
          ? `popularity_${popularityWindow}`
          : `${sortField}_${sortOrder}`;
      if (s !== "created_desc") params.set("sort", s);
      if (popularityWindow !== "all" && sortField === "popularity")
        params.set("pw", popularityWindow);
      history.replace({
        search: params.toString() ? `?${params.toString()}` : "",
      });
    }, 300);
    return () => {
      if (urlSyncTimer.current) clearTimeout(urlSyncTimer.current);
    };
  }, [
    searchTerm,
    selectedFilters,
    filterModes,
    sortField,
    sortOrder,
    popularityWindow,
    page,
    pageSize,
    history,
  ]);

  const sort =
    sortField === "popularity"
      ? `popularity_${popularityWindow}`
      : sortField === "created"
      ? sortOrder === "asc"
        ? "created_asc"
        : "created_desc"
      : sortField === "updated"
      ? sortOrder === "asc"
        ? "updated_asc"
        : "updated_desc"
      : sortField === "price"
      ? sortOrder === "asc"
        ? "price_asc"
        : "price_desc"
      : sortOrder === "asc"
      ? "name_asc"
      : "name_desc";

  const orderLabels =
    sortField === "created" || sortField === "updated"
      ? { asc: "Oldest first", desc: "Newest first" }
      : sortField === "price"
      ? { asc: "Price: Low to High", desc: "Price: High to Low" }
      : { asc: "A to Z", desc: "Z to A" };

  // Pull the catalog's tag + type universe so the Tag / Type filters reflect
  // the real values on products and stay in sync as edits add/remove them. The
  // Category filter is instead sourced from the authoritative WWG Store App
  // Categories tree (same source as the product editor's picker) so its options
  // always match the real category set rather than a hardcoded list.
  React.useEffect(() => {
    let cancelled = false;
    // Fetch each option universe independently: one failing endpoint (e.g.
    // /v1/categories not yet registered in APIM) must not blank the others.
    Promise.allSettled([
      productsApi.listTags(),
      productsApi.listTypes(),
      categoriesApi.list(),
    ]).then(([tagsRes, typesRes, categoriesRes]) => {
      if (cancelled) return;
      const tags = tagsRes.status === "fulfilled" ? tagsRes.value : null;
      const types = typesRes.status === "fulfilled" ? typesRes.value : null;
      const categoryTree =
        categoriesRes.status === "fulfilled" ? categoriesRes.value : null;
      setTagOptions(Array.isArray(tags) ? tags : []);
      setTypeOptions(Array.isArray(types) ? types : []);
      // Flatten the whole tree to { value: code, label: title } — products
      // carry Category Builder *codes* in `storeAppCategories`, so codes
      // (not display titles) are what the products list filters on.
      //
      // Depth + the ancestor path ride along because sibling branches reuse
      // titles: the live tree has a "Bundles" child under Literature, Clothing,
      // Drinkware AND Accessories. A flat list of bare titles renders four
      // identical "Bundles" checkboxes, so the option carries its nesting level
      // (indented in the sidebar) and its full path (shown on hover).
      const flatten = (nodes, out = [], trail = []) => {
        (nodes || []).forEach((node) => {
          if (!node || !node.code) return;
          const title = node.title || node.code;
          const path = [...trail, title];
          out.push({
            value: node.code,
            label: title,
            depth: node.level || path.length,
            hint: path.join(" › "),
          });
          flatten(node.children, out, path);
        });
        return out;
      };
      setCategoryOptions(
        Array.isArray(categoryTree) ? flatten(categoryTree) : []
      );
    });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const activeTags = React.useMemo(
    () => selectedFilters.tag || [],
    [selectedFilters.tag]
  );

  // Show only the most-used tags as quick filters (tags can grow into the
  // hundreds), but always keep any currently-selected tag visible so it stays
  // uncheckable. Free-form tags can still be added via the filter's text input.
  const dynamicFilterGroups = React.useMemo(() => {
    const topTags = tagOptions.slice(0, TOP_TAG_COUNT);
    const tagOpts = Array.from(new Set([...topTags, ...activeTags]));
    return filterGroups.map((g) => {
      if (g.key === "tag") return { ...g, options: tagOpts };
      if (g.key === "productType") return { ...g, options: typeOptions };
      if (g.key === "category" && categoryOptions.length)
        return { ...g, options: categoryOptions };
      return g;
    });
  }, [tagOptions, typeOptions, categoryOptions, activeTags]);

  // Reset to the first page whenever the result set changes. Skip the initial
  // mount so a deep-linked `?p=` page survives the first render instead of being
  // snapped back to page 1.
  const didFilterMountRef = React.useRef(false);
  React.useEffect(() => {
    if (!didFilterMountRef.current) {
      didFilterMountRef.current = true;
      return;
    }
    setPage(1);
  }, [searchTerm, selectedFilters, filterModes, sort, pageSize]);

  // Fetch products whenever search / filters / sort / page change. The mock API
  // filters, sorts and paginates server-side, returning { items, total, page }.
  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    productsApi
      .list({
        search: searchTerm,
        filters: selectedFilters,
        filterModes,
        sort,
        page,
        pageSize,
      })
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        const items = Array.isArray(res) ? res : res?.items || [];
        setProducts(items);
        setTotal(
          Array.isArray(res) ? items.length : res?.total ?? items.length
        );
        setLoading(false);
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setError(errorMessage(err, "Couldn't load the product catalog."));
        setLoading(false);
      });
  }, [
    searchTerm,
    selectedFilters,
    filterModes,
    sort,
    page,
    pageSize,
    refreshKey,
  ]);

  // Clamp the current page into range whenever the result set shrinks — e.g. a
  // deep-linked `?p=999`, or enough items get archived/deleted that the last
  // page no longer exists. Without this the browser strands the user on an
  // empty page while products still exist.
  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Debounce the search box so we don't refetch on every keystroke; the input
  // shows `searchDraft` immediately while `searchTerm` (what we fetch on) lags.
  const handleSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setSearchDraft(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchTerm(value), 300);
  }, []);

  const clearSearch = React.useCallback(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setSearchDraft("");
    setSearchTerm("");
  }, []);

  React.useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    []
  );

  const handleFilterChange = React.useCallback((key, option, checked) => {
    // Tags are normalized so custom-entered values stay consistent with the
    // canonical tag form used everywhere else (matching, highlighting, options).
    const value = key === "tag" ? normalizeTag(option) : option;
    if (key === "tag" && !value) return;
    setSelectedFilters((prev) => {
      const currentValues = prev[key] || [];
      const nextValues = checked
        ? currentValues.includes(value)
          ? currentValues
          : [...currentValues, value]
        : currentValues.filter((v) => v !== value);
      return { ...prev, [key]: nextValues };
    });
  }, []);

  const handleModeChange = React.useCallback((key, mode) => {
    setFilterModes((prev) => ({ ...prev, [key]: mode }));
  }, []);

  const handleClearGroup = React.useCallback((key) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: [] }));
  }, []);

  const handleResetFilters = React.useCallback(() => {
    // "Reset filters" restores the catalog's default filter state (e.g. the
    // Store's default status set and all membership/pin codes included), not an
    // empty set.
    setSelectedFilters(initialFilters);
    setFilterModes(initialFilterModes);
  }, [initialFilters, initialFilterModes]);

  const handleTagClick = React.useCallback((tag) => {
    const normalized = normalizeTag(tag);
    if (!normalized) return;
    setSelectedFilters((prev) => {
      const current = prev.tag || [];
      const next = current.includes(normalized)
        ? current.filter((t) => t !== normalized)
        : [...current, normalized];
      return { ...prev, tag: next };
    });
  }, []);

  const goToPage = React.useCallback(
    (next) => {
      const clamped = Math.min(Math.max(1, next), totalPages);
      if (clamped !== page) {
        setPage(clamped);
        const el = document.getElementById("scrollableDiv");
        if (el) el.scrollTop = 0;
        else window.scrollTo(0, 0);
      }
    },
    [page, totalPages]
  );

  const listItems = React.useMemo(() => products.map(toListItem), [products]);
  const resultsLabel = `${total} ${total === 1 ? "result" : "results"}`;

  return (
    <div className={styles.browser}>
      <div className={styles.searchRow}>
        <div className={`${styles.searchInput} ${styles.searchInputWithHint}`}>
          <input
            type="search"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={searchDraft}
            onChange={handleSearchChange}
            onKeyDown={(event) => {
              if (event.key === "Escape") clearSearch();
            }}
          />
          {searchDraft ? (
            <ClearSearchButton
              className={styles.clearBeforeHint}
              onClick={clearSearch}
            />
          ) : null}
          <span className={styles.searchHint}>
            <button
              type="button"
              className={styles.searchHintButton}
              aria-label="Search tips"
              aria-describedby="search-hint-tip"
            >
              ?
            </button>
            <span
              className={styles.searchHintPopover}
              role="tooltip"
              id="search-hint-tip"
            >
              Tip: start your search with <code>sku:</code> to match SKUs only —
              e.g. <code>sku:WWG-TS</code>
            </span>
          </span>
        </div>
        {toolbarActions}
        <button
          type="button"
          className={styles.sortButton}
          onClick={() => setSortMenuOpen(true)}
          title="Change how the product list is sorted."
        >
          Sort
        </button>
      </div>

      <div className={styles.catalogLayout}>
        <FilterSidebar
          groups={dynamicFilterGroups}
          selectedFilters={selectedFilters}
          onChange={handleFilterChange}
          filterModes={filterModes}
          onModeChange={handleModeChange}
          onClearGroup={handleClearGroup}
          onResetAll={handleResetFilters}
        />

        <div className={styles.productsColumn}>
          {error && (
            <div className={styles.alertWrap}>
              <AlertNotice title="Couldn't load products">{error}</AlertNotice>
            </div>
          )}

          {loading ? (
            <div className={styles.loaderWrap}>
              <Loader size={60} />
            </div>
          ) : (
            <>
              <div className={styles.list} style={{ "--row-gap": "8px" }}>
                <header
                  className={styles.listHeader}
                  style={{ "--action-col-width": actionColWidth }}
                >
                  <span className={styles.colImage}>Image</span>
                  <span className={styles.colTitle}>Title/Info</span>
                  <span className={styles.colSku} title="Stock Keeping Unit">
                    SKU:
                  </span>
                  <span className={styles.colType}>Type:</span>
                  <span className={styles.colStatus}>Status:</span>
                  <span className={styles.resultsCount}>{resultsLabel}</span>
                </header>

                {!listItems.length ? (
                  <div className={styles.emptyState}>
                    <p>
                      No products found. Adjust your filters or search again.
                    </p>
                  </div>
                ) : (
                  <div>
                    {listItems.map((product) => (
                      <ProductItemCard
                        key={product.id}
                        product={product}
                        actionColWidth={actionColWidth}
                        onTagClick={handleTagClick}
                        activeTags={activeTags}
                        onOpen={onItemOpen}
                      >
                        {renderItemActions ? renderItemActions(product) : null}
                      </ProductItemCard>
                    ))}
                  </div>
                )}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={goToPage}
                ariaLabel="Product pages"
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 50, 100]}
                resultsLabel={resultsLabel}
              />
            </>
          )}
        </div>
      </div>

      {sortMenuOpen && (
        <div
          className={styles.sortModalBackdrop}
          onClick={() => setSortMenuOpen(false)}
        >
          <div
            className={styles.sortModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.sortModalHeader}>Sort By</div>
            <div className={styles.sortModalContent}>
              {[
                { value: "created", label: "Date Added" },
                { value: "updated", label: "Last Updated" },
                { value: "product", label: "Product Name" },
                { value: "price", label: "Price" },
                { value: "popularity", label: "Popularity" },
              ].map((opt) => (
                <label className={styles.sortOption} key={opt.value}>
                  <RadioButton
                    name="sort-field"
                    value={opt.value}
                    checked={sortField === opt.value}
                    onChange={() => setSortField(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            <div className={styles.sortModalDivider} />
            {sortField === "popularity" ? (
              <>
                <div className={styles.sortModalHeader}>Time Window</div>
                <div className={styles.sortModalContent}>
                  {[
                    { value: "all", label: "All time" },
                    { value: "7d", label: "Last 7 days" },
                    { value: "30d", label: "Last 30 days" },
                  ].map((opt) => (
                    <label className={styles.sortOption} key={opt.value}>
                      <RadioButton
                        name="popularity-window"
                        value={opt.value}
                        checked={popularityWindow === opt.value}
                        onChange={() => setPopularityWindow(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className={styles.sortModalHeader}>Order</div>
                <div className={styles.sortModalContent}>
                  <label className={styles.sortOption}>
                    <RadioButton
                      name="sort-order"
                      value="asc"
                      checked={sortOrder === "asc"}
                      onChange={() => setSortOrder("asc")}
                    />
                    <span>{orderLabels.asc}</span>
                  </label>
                  <label className={styles.sortOption}>
                    <RadioButton
                      name="sort-order"
                      value="desc"
                      checked={sortOrder === "desc"}
                      onChange={() => setSortOrder("desc")}
                    />
                    <span>{orderLabels.desc}</span>
                  </label>
                </div>
              </>
            )}
            <button
              className={styles.sortModalClose}
              onClick={() => setSortMenuOpen(false)}
              title="Close this sort menu. Your sort choices are applied as you select them."
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

ProductCatalogBrowser.propTypes = {
  renderItemActions: PropTypes.func,
  onItemOpen: PropTypes.func,
  actionColWidth: PropTypes.string,
  toolbarActions: PropTypes.node,
  searchPlaceholder: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  initialFilters: PropTypes.object,
  initialFilterModes: PropTypes.objectOf(PropTypes.string),
  refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ProductCatalogBrowser.defaultProps = {
  renderItemActions: undefined,
  onItemOpen: undefined,
  actionColWidth: "220px",
  toolbarActions: null,
  searchPlaceholder: "Search Products",
  initialFilters: {},
  initialFilterModes: DEFAULT_FILTER_MODES,
  refreshKey: 0,
};

export default ProductCatalogBrowser;
